import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  useTheme,
  CircularProgress,
  Paper,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ChangeCircle as ChangeCircleIcon,
} from "@mui/icons-material";
import Cropper from "react-easy-crop";
import useStyles from "./styles";
import { getCroppedImg } from "../cropUtils";
import InternoModalConfirmacaoSaida from "../../../../../../components/modalSaindoDaPagina";

const MAX_SIZE_MB = 10;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

export default function ModalPingIcon({
  open,
  onClose,
  onSave,
  mode = "create",
  initialData = null,
  setHasUnsavedChanges = () => {},
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [description, setDescription] = useState("");
  const [destino, setDestino] = useState("");
  const [imageSrc, setImageSrc] = useState(null);
  const [file, setFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const fileInputRef = useRef(null);

  const checkHasChanges = useCallback(() => {
    if (mode === "create") {
      return (
        description.trim() !== "" || destino.trim() !== "" || imageSrc !== null
      );
    } else {
      return (
        description.trim() !== (initialData?.descricao || "") ||
        destino.trim() !== (initialData?.metadata?.destino || "") ||
        file !== null
      );
    }
  }, [description, destino, imageSrc, file, mode, initialData]);

  useEffect(() => {
    setHasUnsavedChanges(checkHasChanges());
  }, [checkHasChanges, setHasUnsavedChanges]);

  useEffect(() => {
    if (open) {
      if (mode === "update" && initialData) {
        setDescription(initialData.descricao || "");
        setDestino(initialData.metadata?.destino || "");
        setImageSrc(initialData.imageUrl || null);
        setFile(null);
      } else {
        setDescription("");
        setDestino("");
        setImageSrc(null);
        setFile(null);
      }
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setShowExitConfirm(false);
      setLoading(false);
    }
  }, [open, mode, initialData]);

  const handleCloseRequest = () => {
    if (checkHasChanges()) {
      setShowExitConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowExitConfirm(false);
    onClose();
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) {
      alert("Por favor, selecione um arquivo de imagem válido.");
      return;
    }

    if (selectedFile.size > MAX_SIZE) {
      alert(`O arquivo excede o tamanho máximo de ${MAX_SIZE_MB}MB.`);
      return;
    }

    setFile(selectedFile);
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setImageSrc(reader.result?.toString() || null);
    });
    reader.readAsDataURL(selectedFile);
  };

  useEffect(() => {
    const handlePaste = (e) => {
      if (!open) return;
      if (
        e.clipboardData &&
        e.clipboardData.files &&
        e.clipboardData.files.length > 0
      ) {
        const pastedFile = e.clipboardData.files[0];
        if (pastedFile.type.startsWith("image/")) {
          handleFile(pastedFile);
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChangeFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSave = async () => {
    if (!description.trim()) return;
    if (mode === "create" && !imageSrc) return;

    setLoading(true);

    try {
      let finalFile = null;
      let isNewImage = false;

      // Se há um arquivo novo, vamos cropar
      // Mesmo se for atualização e o usuário não mudou a imagem, nós enviamos sem o arquivo
      // ou se mudou a imagem enviamos o novo crop. Se for a imagem antiga do DB (que já estava lá),
      // e o usuário a editou/cortou de novo (file é null mas imageSrc é blob url e croppedAreaPixels existe)
      // é possível que ele tenha mexido no slider de crop de uma imagem carregada do DB.
      // Se não houver "file" (novo selecionado), não processamos novo crop a não ser que tenhamos modificado para recriar o blob,
      // mas pelo requisito a imagem nova vinda de alteração deve ter um 'file'. Se "file" for nulo, apenas atualizamos descrição.

      if (file && croppedAreaPixels) {
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        finalFile = new File([croppedBlob], file.name || "icon.png", {
          type: "image/png",
        });
        isNewImage = true;
      } else if (!file && croppedAreaPixels && mode === "update") {
        // O usuário deu zoom ou alterou o crop da imagem existente
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        finalFile = new File([croppedBlob], "icon.png", { type: "image/png" });
        isNewImage = true;
      }

      await onSave({
        description: description.trim(),
        destino: destino.trim(),
        file: finalFile,
        isNewImage,
      });
    } catch (e) {
      console.error("Erro ao salvar ícone:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseRequest}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: "16px" },
        }}
      >
        <DialogTitle sx={styles.modalTitle}>
          <Typography variant="h6" fontWeight="bold">
            {mode === "create" ? "Adicionar Novo Ícone" : "Editar Ícone"}
          </Typography>
          <IconButton onClick={handleCloseRequest}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.modalContent}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={imageSrc ? 6 : 12}>
              {!imageSrc ? (
                <Box
                  sx={{
                    ...styles.dropzoneContainer,
                    ...(dragActive ? styles.dropzoneActive : {}),
                  }}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={handleChangeFileClick}
                >
                  <CloudUploadIcon
                    sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    Arraste, cole (Ctrl+V) ou clique para selecionar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Apenas imagens são permitidas (Máximo {MAX_SIZE_MB}MB)
                  </Typography>
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    mb: 3,
                    borderRadius: "8px",
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      overflow: "hidden",
                    }}
                  >
                    <ChangeCircleIcon color="primary" sx={{ fontSize: 32 }} />
                    <Box sx={{ overflow: "hidden" }}>
                      <Typography variant="subtitle2" fontWeight="bold" noWrap>
                        Arquivo Selecionado
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {file ? file.name : "Imagem atual (do banco)"}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleChangeFileClick}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      minWidth: "max-content",
                      ml: 2,
                    }}
                  >
                    Alterar
                  </Button>
                </Paper>
              )}

              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept="image/png, image/jpeg, image/svg+xml, image/webp"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFile(e.target.files[0]);
                  }
                  e.target.value = null;
                }}
              />

              <TextField
                fullWidth
                label="Descrição do Destino (ex: Google, Open DNS)"
                variant="outlined"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={styles.descriptionField}
              />

              <TextField
                fullWidth
                label="Destino (IP/Link) (ex: 8.8.8.8)"
                variant="outlined"
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                sx={styles.descriptionField}
              />
            </Grid>

            {imageSrc && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Ajustar Imagem
                </Typography>
                <Box sx={styles.cropContainer}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    restrictPosition={false}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </Box>
                <Box sx={styles.zoomSliderContainer}>
                  <ZoomOutIcon sx={{ color: "text.secondary" }} />
                  <input
                    type="range"
                    value={zoom}
                    min={0.1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e) => setZoom(Number(e.target.value))}
                    style={{
                      flex: 1,
                      margin: "0 16px",
                      cursor: "pointer",
                      accentColor: theme.palette.primary.main,
                    }}
                  />
                  <ZoomInIcon sx={{ color: "text.secondary" }} />
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={styles.actionsContainer}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                loading ||
                !description.trim() ||
                (mode === "create" && !imageSrc)
              }
            >
              {loading ? <CircularProgress size={24} /> : "Salvar"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <InternoModalConfirmacaoSaida
        openConfirmacao={showExitConfirm}
        setOpenConfirmacao={setShowExitConfirm}
        title="Atenção"
        headerText="Existem alterações não salvas."
        contentText="Deseja realmente sair? As alterações não salvas serão perdidas."
        confirmText="Sair"
        execute={handleConfirmClose}
        cancel={() => setShowExitConfirm(false)}
        severity="warning"
      />
    </>
  );
}
